import { create } from "zustand";

import type { item as BreadcrumbItem } from "@/components/Breadcrumb";
import { PRODUCT_GROUPS } from "@/constants/pages";

export enum HistoryItemTypeEnum {
  PRODUCT_GROUP = "PRODUCT_GROUP",
  THEME = "THEME",
  PRODUCT = "PRODUCT",
  STORE = "STORE",
}

export interface HistoryItem {
  name: string;
  type: HistoryItemTypeEnum;
  route: string;
}

export interface ProductStoreBreadcrumbStore {
  history: HistoryItem[];
  handleNewHistoryEntry: (pathname: string) => void;
  getBreadcrumbItems: () => BreadcrumbItem[];
  getItemFromType: (type: HistoryItemTypeEnum) => HistoryItem | null;
  setItemNameFromType: (type: HistoryItemTypeEnum, name: string) => void;
  isUserInGalleryPage: () => boolean;
  reset: () => void;
}

const isProductGalleryPage = (item?: HistoryItem | null) =>
  [HistoryItemTypeEnum.THEME, HistoryItemTypeEnum.PRODUCT_GROUP].includes(item?.type as HistoryItemTypeEnum);

const isProductOrStorePage = (item?: HistoryItem | null) =>
  [HistoryItemTypeEnum.PRODUCT, HistoryItemTypeEnum.STORE].includes(item?.type as HistoryItemTypeEnum);

const getHistoryItem = (pathname: string): HistoryItem | null => {
  const productGroup = PRODUCT_GROUPS.find((group) => group.pathname === pathname);

  if (productGroup) {
    return {
      route: pathname,
      name: productGroup.pageName,
      type: HistoryItemTypeEnum.PRODUCT_GROUP,
    };
  }

  const pathnameParts = pathname.split("/").filter((part) => part);

  if (pathnameParts[0] === "temas") {
    return { route: pathname, name: "TEMA", type: HistoryItemTypeEnum.THEME }; // TODO: Get theme name
  }

  if (pathnameParts[0] === "lojas") {
    return { route: pathname, name: "", type: HistoryItemTypeEnum.STORE };
  }

  if (pathnameParts.length === 1) {
    return {
      route: pathname,
      name: "",
      type: HistoryItemTypeEnum.PRODUCT,
    };
  }

  return null;
};

export const useProductStoreBreadcrumbStore = create<ProductStoreBreadcrumbStore>((set, get) => ({
  history: [],
  handleNewHistoryEntry: (pathname) => {
    const { history } = get();
    const historyItem = getHistoryItem(pathname);
    const previousHistoryItem = get().history.slice(-1)[0];

    if (!historyItem) {
      get().reset();

      return;
    }

    if (previousHistoryItem?.route !== historyItem.route) {
      const userWentBack = history.slice(-2)[0]?.route === historyItem.route;

      if (userWentBack) {
        set({ history: history.slice(0, -1) });

        return;
      }

      const userIsInProductGallery = isProductGalleryPage(historyItem);
      const userStartedFromProductGallery = isProductGalleryPage(history[0]);
      const userIsInAnotherProduct =
        historyItem.type === HistoryItemTypeEnum.PRODUCT &&
        history.some(({ type }) => HistoryItemTypeEnum.PRODUCT === type);
      const userIsInAnotherStoreOrProduct =
        isProductOrStorePage(historyItem) && history.some((item) => isProductOrStorePage(item));

      if (userIsInProductGallery || (!userStartedFromProductGallery && userIsInAnotherStoreOrProduct)) {
        set({ history: [historyItem] });

        return;
      }

      if (userStartedFromProductGallery && userIsInAnotherProduct) {
        set({ history: [...history.slice(0, -1), historyItem] });

        return;
      }

      set({
        history: [...history, historyItem],
      });
    }
  },
  getBreadcrumbItems: () => {
    return get()
      .history.filter((item) => item.name)
      .map((item, index) => ({
        text:
          isProductGalleryPage(item) && history.length === 1 ? (
            <span className="font-normal">
              Resultado de busca por <b>{item.name}</b>
            </span>
          ) : (
            item.name
          ),
        href: index < get().history.length - 1 ? item.route : undefined,
      }));
  },
  getItemFromType: (type) => get().history.find((item) => item.type === type) || null,
  setItemNameFromType: (type, name) => {
    set({
      history: get().history.map((item) => ({
        ...item,
        name: item.type === type ? name : item.name,
      })),
    });
  },
  isUserInGalleryPage: () => isProductGalleryPage(get().history.slice(-1)[0]),
  reset: () => {
    set({ history: [] });
  },
}));
